<template>
    <div class="billing-page" :class="{ 'billing-page--light': !dark }">
        <template v-if="currentTab === 'myBillingAccounts'">
            <section class="billing-page__hero">
                <div>
                    <div class="billing-page__eyebrow">Billing</div>
                    <h1>Billing Accounts</h1>
                    <p>
                        Manage subscriptions, payment methods and billing
                        profiles across your organisation.
                    </p>
                </div>
                <v-btn color="primary" @click="openCreateForm">
                    <v-icon start>$vuetify.icons.plus</v-icon>
                    New Billing Account
                </v-btn>
            </section>

            <section class="billing-page__metrics">
                <billing-metric-card
                    icon="mdi-domain"
                    label="Billing Accounts"
                    :value="billingAccounts.length"
                    caption="active accounts"
                    tone="purple"
                />
                <billing-metric-card
                    icon="mdi-credit-card-outline"
                    label="Active Subscriptions"
                    :value="activeSubscriptionCount"
                    caption="currently billed"
                    tone="green"
                />
                <billing-metric-card
                    icon="$vuetify.icons.team"
                    label="Teams"
                    :value="teamTotal"
                    caption="linked teams"
                    tone="orange"
                />
                <billing-metric-card
                    icon="mdi-database-outline"
                    label="Datasets"
                    :value="datasetTotal"
                    caption="attached datasets"
                    tone="cyan"
                />
            </section>

            <billing-panel class="billing-page__toolbar">
                <v-text-field
                    v-model="search"
                    density="compact"
                    hide-details
                    prepend-inner-icon="mdi-magnify"
                    placeholder="Search billing accounts..."
                    variant="outlined"
                    class="billing-page__search"
                />

                <v-btn-toggle
                    v-model="statusFilter"
                    mandatory
                    density="comfortable"
                    class="billing-page__status-tabs"
                >
                    <v-btn value="all">All</v-btn>
                    <v-btn value="active">Active</v-btn>
                    <v-btn value="trial">Trial</v-btn>
                    <v-btn value="past_due">Past Due</v-btn>
                    <v-btn value="suspended">Suspended</v-btn>
                </v-btn-toggle>

                <v-select
                    v-model="planFilter"
                    :items="planOptions"
                    density="compact"
                    hide-details
                    item-title="text"
                    item-value="value"
                    variant="outlined"
                    class="billing-page__select"
                />
                <v-select
                    v-model="sortBy"
                    :items="sortOptions"
                    density="compact"
                    hide-details
                    item-title="text"
                    item-value="value"
                    variant="outlined"
                    class="billing-page__select"
                />
                <v-btn-toggle
                    v-model="viewMode"
                    mandatory
                    density="comfortable"
                    class="billing-page__view-toggle"
                >
                    <v-btn value="grid" icon="mdi-view-grid-outline" />
                    <v-btn value="list" icon="mdi-format-list-bulleted" />
                </v-btn-toggle>
            </billing-panel>

            <div class="billing-page__count">
                {{ filteredAccounts.length }} billing accounts
            </div>

            <section
                class="billing-page__accounts"
                :class="{ 'billing-page__accounts--list': viewMode === 'list' }"
            >
                <billing-account-card
                    v-for="billingAccount in filteredAccounts"
                    :key="billingAccount.uuid || billingAccount.id"
                    :account="billingAccount"
                />

                <billing-panel
                    class="billing-page__create-card"
                    @click="openCreateForm"
                >
                    <span class="billing-page__create-icon">
                        <v-icon size="30">$vuetify.icons.plus</v-icon>
                    </span>
                    <h2>Create Billing Account</h2>
                    <p>Add a new billing profile for your organisation.</p>
                    <v-btn color="primary">Create Account</v-btn>
                </billing-panel>
            </section>
        </template>

        <template v-else>
            <billing-panel class="billing-page__mobile-form">
                <billing-account-form
                    @tab-navigation="currentTab = 'myBillingAccounts'"
                />
            </billing-panel>
        </template>

        <v-dialog
            v-model="showCreateForm"
            max-width="680px"
            hide-overlay
            color="grey darken-3"
        >
            <v-card class="billing-page__dialog elevation-0">
                <div class="billing-page__dialog-header">
                    <div>
                        <div class="billing-page__eyebrow">Billing</div>
                        <h2>Create New Billing Account</h2>
                    </div>
                    <v-btn variant="text" icon @click="showCreateForm = false">
                        <v-icon>$vuetify.icons.close</v-icon>
                    </v-btn>
                </div>
                <billing-account-form
                    @tab-navigation="currentTab = 'myBillingAccounts'"
                    @cancel="showCreateForm = false"
                />
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import BillingAccountCard from 'components/BillingAccounts/Shared/BillingAccountCard.vue';
import BillingAccountForm from 'components/BillingAccounts/BillingAccountForm/BillingAccountForm.vue';
import BillingMetricCard from 'components/BillingAccounts/Shared/BillingMetricCard.vue';
import BillingPanel from 'components/BillingAccounts/Shared/BillingPanel.vue';
import { useDisplay } from 'vuetify';

export default {
    name: 'MyBillingAccounts',
    components: {
        BillingAccountCard,
        BillingAccountForm,
        BillingMetricCard,
        BillingPanel,
    },
    props: {
        dark: {
            type: Boolean,
            default: false,
        },
        billingAccounts: {
            type: Array,
            default: function () {
                return [];
            },
        },
    },
    setup() {
        const { mdAndUp } = useDisplay();
        return { mdAndUp };
    },
    data: function () {
        return {
            currentTab: 'myBillingAccounts',
            showCreateForm: false,
            search: '',
            statusFilter: 'all',
            planFilter: 'all',
            sortBy: 'recent',
            viewMode: 'grid',
            sortOptions: [
                { text: 'Sort: Recent', value: 'recent' },
                { text: 'Sort: Name', value: 'name' },
                { text: 'Sort: Plan', value: 'plan' },
            ],
        };
    },
    computed: {
        activeSubscriptionCount() {
            return this.billingAccounts.filter((account) => {
                const status = this.accountStatus(account);
                return status === 'active' || status === 'trialing';
            }).length;
        },
        teamTotal() {
            return this.billingAccounts.reduce(
                (total, account) => total + Number(account.TeamCount || 0),
                0
            );
        },
        datasetTotal() {
            return this.billingAccounts.reduce(
                (total, account) =>
                    total + Number(account.billingAssetCount || 0),
                0
            );
        },
        planOptions() {
            const plans = this.billingAccounts
                .map(
                    (account) => account.billingPlan && account.billingPlan.name
                )
                .filter(Boolean)
                .sort();
            return [
                { text: 'All Plans', value: 'all' },
                ...Array.from(new Set(plans)).map((plan) => ({
                    text: plan,
                    value: plan,
                })),
            ];
        },
        filteredAccounts() {
            const query = this.search.trim().toLowerCase();
            const accounts = this.billingAccounts.filter((account) => {
                const haystack = [
                    account.companyName,
                    account.contactName,
                    account.billingEmail,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                const matchesSearch = !query || haystack.includes(query);
                const matchesStatus =
                    this.statusFilter === 'all' ||
                    this.accountStatus(account) === this.statusFilter;
                const planName =
                    account.billingPlan && account.billingPlan.name
                        ? account.billingPlan.name
                        : '';
                const matchesPlan =
                    this.planFilter === 'all' || planName === this.planFilter;
                return matchesSearch && matchesStatus && matchesPlan;
            });

            return accounts.slice().sort((a, b) => {
                if (this.sortBy === 'name') {
                    return (a.companyName || '').localeCompare(
                        b.companyName || ''
                    );
                }
                if (this.sortBy === 'plan') {
                    return this.planName(a).localeCompare(this.planName(b));
                }
                return 0;
            });
        },
    },
    methods: {
        openCreateForm() {
            if (this.mdAndUp) {
                this.showCreateForm = true;
            } else {
                this.currentTab = 'newBillingAccount';
            }
        },
        accountStatus(account) {
            return String(account.subscriptionStatus || 'active').toLowerCase();
        },
        planName(account) {
            return account.billingPlan && account.billingPlan.name
                ? account.billingPlan.name
                : '';
        },
    },
};
</script>

<style scoped>
.billing-page {
    --billing-bg:
        radial-gradient(
            circle at top right,
            rgba(42, 70, 171, 0.24),
            transparent 34rem
        ),
        #061321;
    --billing-card-bg: rgba(20, 36, 56, 0.78);
    --billing-border: rgba(153, 162, 185, 0.22);
    --billing-border-soft: rgba(153, 162, 185, 0.14);
    --billing-muted: #aab5c8;
    --billing-text: #f6f9ff;
    background: var(--billing-bg);
    color: var(--billing-text);
    min-height: calc(100vh - 96px);
    padding: 40px clamp(18px, 6vw, 96px) 56px;
}

.billing-page--light {
    --billing-bg: #f7f9fe;
    --billing-card-bg: #ffffff;
    --billing-border: #e0e7f4;
    --billing-border-soft: #edf1f8;
    --billing-muted: #6f778a;
    --billing-text: #172033;
}

.billing-page__hero {
    align-items: flex-start;
    display: flex;
    gap: 24px;
    justify-content: space-between;
    margin: 0 auto 28px;
    max-width: 1280px;
}

.billing-page__eyebrow {
    color: #3c64f4;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.14em;
    margin-bottom: 8px;
    text-transform: uppercase;
}

.billing-page h1,
.billing-page h2 {
    color: var(--billing-text);
    font-weight: 800;
    line-height: 1.15;
    margin: 0;
}

.billing-page h1 {
    font-size: 30px;
}

.billing-page__hero p {
    color: var(--billing-muted);
    font-size: 15px;
    line-height: 1.55;
    margin: 10px 0 0;
    max-width: 480px;
}

.billing-page__metrics,
.billing-page__accounts {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin: 0 auto 18px;
    max-width: 1280px;
}

.billing-page__toolbar {
    align-items: center;
    display: flex;
    gap: 12px;
    margin: 0 auto 18px;
    max-width: 1280px;
    padding: 12px;
}

.billing-page__search {
    flex: 1 1 280px;
}

.billing-page__status-tabs {
    flex: 0 1 auto;
}

.billing-page__select {
    flex: 0 0 150px;
}

.billing-page__count {
    color: var(--billing-muted);
    font-size: 14px;
    margin: 0 auto 12px;
    max-width: 1280px;
}

.billing-page__accounts {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.billing-page__accounts--list {
    grid-template-columns: 1fr;
}

.billing-page__create-card {
    align-items: center;
    border-style: dashed;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 230px;
    padding: 28px;
    text-align: center;
}

.billing-page__create-icon {
    align-items: center;
    border: 2px solid #9eb2fa;
    border-radius: 999px;
    color: #9eb2fa;
    display: inline-flex;
    height: 48px;
    justify-content: center;
    margin-bottom: 14px;
    width: 48px;
}

.billing-page__create-card h2 {
    font-size: 18px;
}

.billing-page__create-card p {
    color: var(--billing-muted);
    margin: 8px 0 18px;
}

.billing-page__dialog {
    background: var(--billing-card-bg);
    color: var(--billing-text);
    padding: 18px;
}

.billing-page__dialog-header {
    align-items: flex-start;
    display: flex;
    justify-content: space-between;
    padding: 6px 8px 8px 20px;
}

.billing-page__mobile-form {
    margin: 0 auto;
    max-width: 720px;
    padding: 18px;
}

@media (max-width: 1180px) {
    .billing-page__metrics,
    .billing-page__accounts {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .billing-page__toolbar {
        align-items: stretch;
        flex-wrap: wrap;
    }
}

@media (max-width: 760px) {
    .billing-page {
        padding: 24px 14px 40px;
    }

    .billing-page__hero {
        flex-direction: column;
    }

    .billing-page__metrics,
    .billing-page__accounts,
    .billing-page__accounts--list {
        grid-template-columns: 1fr;
    }

    .billing-page__status-tabs,
    .billing-page__select,
    .billing-page__view-toggle {
        width: 100%;
    }
}
</style>
